/**
 * Pipeline cards for decks that are still being analysed — and for those that
 * failed.
 *
 * The failed state is the reason this component exists. Before 2026-08-01 a
 * deck that broke the analysis produced NOTHING: no card, no error, no trace.
 * An 18.5 MB deck killed the edge isolate outright and the investor was left
 * looking at an unchanged pipeline, with no way to tell "still working" from
 * "gone". A progress indicator alone would have reproduced that — it would just
 * have spun forever.
 */

import React from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import { FileSearch, AlertCircle, RotateCw, Building2, ArrowRight } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import {
  describeAnalysis,
  isClosedCompanyMatch,
  useConfirmDeckAnalysisDifferentCompany,
  type DeckAnalysisCard,
} from '@/lib/investor/use-deck-analyses';
import { useAlert } from '@/components/AlertDialog';
import { handleError } from '@/lib/errors';

interface Props {
  analyses: DeckAnalysisCard[];
  onRetry: () => void;
}

export function DeckAnalysisCards({ analyses, onRetry }: Props) {
  const colors = useThemeColors();
  const { error: showError } = useAlert();
  // 119-07: a deck matching a company that closed on Inverio. Confirming it is
  // a different company re-posts THIS analysis (same id, same stored deck);
  // the ordinary Retry would upload a new one and stop at the same match.
  const confirm = useConfirmDeckAnalysisDifferentCompany();
  const confirmingId = confirm.isPending ? confirm.variables?.id ?? null : null;
  const onConfirmDifferentCompany = (card: DeckAnalysisCard) => {
    confirm.mutate(card, {
      onError: (err) => {
        const { message, isCancelled } = handleError(err, { context: 'DeckAnalysisCards.confirmDifferentCompany' });
        if (!isCancelled) showError("Couldn't start the analysis", message);
      },
    });
  };

  if (analyses.length === 0) return null;

  return (
    <View className="px-5 mb-2">
      {analyses.map((card) => {
        const { label, detail } = describeAnalysis(card.state);
        // A closed-company match is a question, not a failure: amber, and the
        // action is the investor's answer rather than a retry.
        const matched = isClosedCompanyMatch(card.state);
        const failed = card.state.status === 'failed' && !matched;
        const accent = matched ? colors.amber.main : failed ? colors.error : colors.primary[600];
        const surface = matched ? colors.amber.light : failed ? colors.traffic.redLight : colors.primary[50];
        const border = matched ? colors.amber.muted : failed ? colors.error : colors.primary[200];
        const confirming = confirmingId === card.id;

        return (
          <Animated.View
            key={`analysis-${card.id}`}
            entering={FadeInUp.duration(300)}
            className="rounded-2xl p-4 mb-3 overflow-hidden"
            style={{ backgroundColor: surface, borderWidth: 1, borderColor: border }}
          >
            <View className="flex-row items-center gap-3">
              <View
                className="w-11 h-11 rounded-xl items-center justify-center"
                style={{ backgroundColor: matched ? colors.surface.primary : failed ? colors.traffic.redLight : colors.primary[100] }}
              >
                {matched ? (
                  <Building2 size={22} color={colors.amber.main} />
                ) : failed ? (
                  <AlertCircle size={22} color={colors.error} />
                ) : (
                  <FileSearch size={22} color={colors.primary[600]} />
                )}
              </View>

              <View className="flex-1">
                <View className="flex-row items-center gap-2">
                  {!failed && !matched && <ActivityIndicator size="small" color={accent} />}
                  <Text
                    style={{ color: accent }}
                    className="font-semibold flex-shrink"
                    numberOfLines={1}
                  >
                    {label}
                  </Text>
                </View>
                {/* The filename is all we can name it by until extraction
                    resolves a company — naming it after the file is honest,
                    inventing a company name would not be. */}
                <Text
                  style={{ color: colors.text.secondary }}
                  className="text-sm"
                  numberOfLines={1}
                >
                  {card.fileName}
                </Text>
                <Text
                  style={{ color: colors.text.tertiary }}
                  className="text-xs mt-0.5"
                  numberOfLines={matched ? 3 : 2}
                >
                  {detail}
                </Text>
              </View>

              {failed && (
                <View
                  className="rounded-lg overflow-hidden"
                  style={{ height: 36, backgroundColor: colors.error }}
                >
                  {/* Dimensions live on the wrapper: a callback `style` on
                      Pressable does not compute height reliably on RN 0.81+. */}
                  <AnimatedPressable
                    onPress={onRetry}
                    accessibilityRole="button"
                    accessibilityLabel={`Try analysing ${card.fileName} again`}
                    className="flex-1 px-3 flex-row items-center justify-center gap-1"
                  >
                    <RotateCw size={14} color="white" />
                    <Text className="text-white font-medium text-sm" numberOfLines={1}>
                      Retry
                    </Text>
                  </AnimatedPressable>
                </View>
              )}
            </View>

            {matched && (
              <View
                className="rounded-xl overflow-hidden mt-3"
                style={{ height: 44, backgroundColor: colors.amber.main, opacity: confirming ? 0.7 : 1 }}
              >
                {/* Dimensions on the wrapper (pressable_sizing). */}
                <AnimatedPressable
                  testID={`deck-analysis-confirm-${card.id}`}
                  onPress={() => {
                    if (!confirming) onConfirmDifferentCompany(card);
                  }}
                  disabled={confirming}
                  accessibilityRole="button"
                  accessibilityLabel={`${card.fileName} is a different company; analyse it`}
                  className="flex-1 px-4 flex-row items-center justify-center gap-2"
                >
                  {confirming ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <ArrowRight size={16} color="white" />
                  )}
                  <Text className="text-white font-semibold text-sm" numberOfLines={1}>
                    Different company, analyse
                  </Text>
                </AnimatedPressable>
              </View>
            )}
          </Animated.View>
        );
      })}
    </View>
  );
}
